import React, { FC, useEffect, useState } from 'react';
import moment from 'moment';
import { EmployeeContributionsTable } from './EmployeeContributionsTable';
import { EmployerContributionsTable } from './EmployerContributionsTable';
import { DefinedContributionsTable } from './DefinedContributionsTable';
import { ContentCard } from '../Cards';
import { fetchCartInfo, toggleCart } from '../../actions/CartActions';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { FaAngleDoubleRight } from 'react-icons/fa';
import "./Cart.scss";
import { EnrollmentPackageId, GroupId } from '../../types/api/primaryKeys/PrimaryKey';
import { useWolfTracsState } from '../../hooks/redux/useWolfTracsState';
import { useReduxDispatch } from '../../hooks/redux/useReduxDispatch';
import { PackageCartSummary } from '../../types/api/cart/PackageCartSummary';
import { getGroupImageBlob } from '../../apiCalls/images/imageApiCalls';
import { Nullable } from '../../types/custom/Nullable';
import { useMounted } from '../../hooks/components/useMounted';

interface Props {
    packageId: EnrollmentPackageId;
}

const Cart: FC<Props> = ({ packageId }) => {

    // Redux State
    const { activeCart: cart } = useWolfTracsState(s => s.cart);
    const { cartPanel } = useWolfTracsState(s => s.cart);
    const { data: activePackage, loading: activePackageIsLoading } = useWolfTracsState(s => s.packages.activePackage);

    // Dispatch
    const dispatch = useReduxDispatch();

    // Local State
    const [ groupImageUrl, setGroupImageUrl ] = useState<Nullable<string>>(null);

    // Mounting
    const isMounted = useMounted();

    // Populate cart info on component load.
    useEffect(() => {
        dispatch(fetchCartInfo(packageId));
    }, [packageId]);


    // Code to refresh the group image displayed in the cart.
    useEffect(() => {
        async function pullImage(groupId: GroupId, fileName: string) {
            const blob = await getGroupImageBlob(groupId, fileName);
            const url = URL.createObjectURL(blob);

            if (isMounted) {
                setGroupImageUrl(url);
            }
        }

        if (activePackage?.groupLogoFileName) {
            pullImage(activePackage.groupId, activePackage.groupLogoFileName);
        }
        else if (!activePackageIsLoading) {
            // Note: Only removing the image if there's no logo AND we aren't currently loading one - prevents re-renders
            // in the case the active package in state re-loads, but the logo won't be changing.

            // This must be done so image disappears when moving between group with image & group with no image
            // (which is probably "impossible" in enrollment in one render, but still...)
            setGroupImageUrl(null);
        }
    }, [activePackage?.groupId, activePackage?.groupLogoFileName, activePackageIsLoading]);

    // Helpers
    const getEmployeeTotal = (data: readonly PackageCartSummary[]) => {
        let t = 0;
        data.forEach((d) => {
            if (d.employeePreTaxContribution > 0) {
                t += d.employeePreTaxContribution;
            } else if(d.employeePostTaxContribution) {
                t += d.employeePostTaxContribution;
            }
        });

        return t.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
    };

    const getEmployerTotal = (data: readonly PackageCartSummary[]) => {
        let t = 0;
        data.forEach((d) => {
            t += d.employerContribution;
        });
        return t.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
    };

    // Check for explicit true...not undefined / not loaded.
    const hideEmployeeContributions = activePackage === null || activePackage.hideEmployeeContributionInEnrollment;
    const hideEmployerContributions = activePackage === null || activePackage.hideEmployerContributionInEnrollment;

    // Main Render Logic
    const cartWrapper = cartPanel.opened ? "cart-wrapper opened" : "cart-wrapper";
    return (
        <div className={cartWrapper}>
            <ContentCard loading={cart.loading}>
                <div className="backdrop" onClick={() => dispatch(toggleCart())}></div>
                <Container className="sidecart-nav justify-content-end">
                    <Row>
                        <Col>
                            <Button onClick={() => dispatch(toggleCart())}><FaAngleDoubleRight /> Close</Button>
                        </Col>
                    </Row>
                </Container>
                <div className="cart">
                    {groupImageUrl && 
                        <div className="logo-container logo text-center">
                            <img src={groupImageUrl} alt="logo" />
                        </div>
                    }
                    <div className="label text-center mt-3 mb-3">Enrollment Deadline: {!activePackage ? '...' : moment(activePackage.endDate).format("YYYY-MM-DD")}</div>
                    {cart.data?.cartItems && (cart.data.cartItems.length > 0 || cart.data.definedContributionAvailable > 0) && 
                        <React.Fragment>
                        {(cart.data.cartItems.filter(e => e.definedContribution > 0).length > 0 || cart.data.definedContributionAvailable > 0)  &&
                            <DefinedContributionsTable 
                                available={cart.data.definedContributionAvailable}
                                used={cart.data.definedContributionUsed}
                                remaining={cart.data.definedContributionRemaining}
                            />}
                        {!hideEmployeeContributions && cart.data.cartItems.filter(e => e.employeePostTaxContribution > 0 || e.employeePreTaxContribution > 0).length > 0 && 
                            <EmployeeContributionsTable cart={cart.data.cartItems.filter(e => e.employeePostTaxContribution > 0 || e.employeePreTaxContribution > 0)} total={getEmployeeTotal(cart.data.cartItems)} /> }

                        {!hideEmployerContributions && cart.data.cartItems.filter(e => e.employerContribution > 0).length > 0 && 
                            <EmployerContributionsTable cart={cart.data.cartItems.filter(e => e.employerContribution > 0)} total={getEmployerTotal(cart.data.cartItems)} />}
                    
                            <div className="label-info text-center mt-3 mb-3">
                                * All contribution are per paycheck
                            </div>
                        </React.Fragment>
                    }
                </div>
            </ContentCard>
        </div>
    );
};

export default Cart;